import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Text } from 'rebass/styled-components';
import { Field, Form, Formik, FormikContextType, FormikHelpers } from 'formik';

import SubmissionModuleArray from 'features/submissions/components/organisms/SubmissionModuleArray';
import RefreshPrompt from 'shared/components/atoms/RefreshPrompt';
import ResponsiveDisplay from 'shared/components/atoms/ResponsiveDisplay';
import { Box, Flex } from 'shared/components/display';
import { RichtextInput } from 'shared/components/Richtext';

import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { OnSubmit } from 'shared/lib/formik';
import { intl, useIntl } from 'shared/lib/intl';
import { TEXT_VARIANTS } from 'shared/styles/text';
import { Brief } from 'shared/typings/briefs';
import { Submission, SubmissionStatus } from 'shared/typings/submissions';

import SubmissionFormButtons from '../molecules/SubmissionFormButtons';
import SubmissionFormNavigationPrompt from '../molecules/SubmissionFormNavigationPrompt.tsx';

import styles from './styles';
import { SubmissionFormFields, SubmissionFormState, SubmissionModuleFields, SubmitValues } from './types';
import { formatInitialValues, formatSubmitValues } from './utils';
import { submissionSchema } from './validation';

interface Props {
  brief: Brief;
  onSubmit: (values: SubmitValues) => void;
  onDraftSubmit: (values: SubmitValues) => void;
  submission: Submission;
  isEdit?: boolean;
  loadingUpdatePublishSubmission?: boolean;
  loadingUpdateDraftSubmission?: boolean;
}

const SubmissionForm: React.FC<Props> = ({
  brief,
  onSubmit,
  onDraftSubmit,
  submission,
  loadingUpdatePublishSubmission = false,
  loadingUpdateDraftSubmission = false,
}) => {
  const { currentUser } = useCurrentUser();
  const initialValues = useMemo(() => formatInitialValues(submission, brief), [submission, brief]);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const isSubmitted = submission.status === SubmissionStatus.SUBMITTED;
  const [formCompleted, setFormCompleted] = useState(false);

  const handleSubmit: OnSubmit<SubmissionFormState> = (_, actions: FormikHelpers<SubmissionFormState>) => {
    // Because we've got multiple "ways to submit" we're going to
    // reset submitting state in case we cancel submitting
    actions.setSubmitting(false);
  };

  const postSubmission: OnSubmit<SubmissionFormState> = (state, helpers) => {
    setFormCompleted(true);
    onSubmit(formatSubmitValues(submission, state));
  };

  const touchAllFields = ({ values, setFieldValue }: FormikContextType<SubmissionFormState>) => {
    const field = SubmissionFormFields.MODULE_ASSETS;
    const touched = SubmissionModuleFields.TOUCHED;
    values.moduleAssets.forEach((_, index) => {
      setFieldValue(`${field}[${index}].${touched}`, true);
    });
  };

  return (
    <Formik<SubmissionFormState>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={submissionSchema}
      validateOnChange
      enableReinitialize
    >
      {(helpers) => (
        <Box css={styles} className="submission-form">
          <RefreshPrompt />
          <SubmissionFormNavigationPrompt<SubmissionFormState> active={!formCompleted} />
          <Form className="submission-form__form">
            <Flex className="submission-form__header">
              <Text variant={TEXT_VARIANTS.H3}>
                {intl.formatMessage({
                  id: 'HEADING_SUBMISSION_CREATION',
                })}
              </Text>
              <ResponsiveDisplay display={'desktop'}>
                <SubmissionFormButtons
                  primaryOnClick={() => touchAllFields(helpers)}
                  isSubmitted={isSubmitted}
                  secondaryOnClick={
                    isSubmitted
                      ? () => navigate(-1)
                      : ({ values }) => {
                          setFormCompleted(true);
                          onDraftSubmit(formatSubmitValues(submission, values));
                        }
                  }
                  onConfirm={(values) => postSubmission(values, helpers)}
                  loadingUpdateDraftSubmission={loadingUpdateDraftSubmission}
                  loadingUpdatePublishSubmission={loadingUpdatePublishSubmission}
                />
              </ResponsiveDisplay>
            </Flex>
            <hr className="submission-form__divider" />
            <Flex className="submission-form__body">
              <Text
                variant={TEXT_VARIANTS.SUBHEADING}
                className="submission-form__label-comment"
                id={SubmissionFormFields.DESCRIPTION}
                as="label"
              >
                {formatMessage({ id: 'LABEL_COMMENT' })}
              </Text>
              <Field
                name={SubmissionFormFields.DESCRIPTION}
                component={RichtextInput}
                placeholder={formatMessage({ id: 'LABEL_COMMENT' })}
                ariaLabelledBy={SubmissionFormFields.DESCRIPTION}
              />
              <SubmissionModuleArray name={SubmissionFormFields.MODULE_ASSETS} userId={currentUser.id} />
            </Flex>
            <ResponsiveDisplay display={'mobile'}>
              <SubmissionFormButtons
                primaryOnClick={() => touchAllFields(helpers)}
                isSubmitted={isSubmitted}
                secondaryOnClick={
                  isSubmitted
                    ? () => navigate(-1)
                    : ({ values }) => {
                        setFormCompleted(true);
                        onDraftSubmit(formatSubmitValues(submission, values));
                      }
                }
                onConfirm={(values) => postSubmission(values, helpers)}
                loadingUpdateDraftSubmission={loadingUpdateDraftSubmission}
                loadingUpdatePublishSubmission={loadingUpdatePublishSubmission}
              />
            </ResponsiveDisplay>
          </Form>
        </Box>
      )}
    </Formik>
  );
};

export default SubmissionForm;
